import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';

const ALL_COLORS: string[][] = [
  [
    '#a1b74f',
    '#b4c094',
    '#c8c7cf',
    '#d3c9fd',
    '#a5b4ee',
    '#759fdf',
    '#3d89d0',
    '#7982bd',
    '#af78a8',
    '#dc6691',
  ],
  [
    '#aaffc2',
    '#c3ffd4',
    '#daffe5',
    '#e8ffe9',
    '#ecffdd',
    '#f1ffd1',
    '#f5ffc5',
    '#f6ffb7',
    '#f4ffa8',
    '#f2ff98',
  ],
  [
    '#7cbfc3',
    '#93c6b1',
    '#a8cc9d',
    '#bdd286',
    '#d1d769',
    '#e0d258',
    '#e8c35a',
    '#f0b45b',
    '#f6a45d',
    '#fb945d',
  ],
];

// CMYK(0,0,0,0) = white, opacity 0.8 — drawn over eggs on answer pages to obscure the images
const OVERLAY = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0, 0.8));

function drawEgg(
  page: PageGen,
  rect: PdfRect,
  brush: PdfBrush | null,
  pen: typeof GUtil.basePen | null
): void {
  const { left, right, top, bottom, width: W, height: H } = rect;

  // Two cubic beziers forming a closed egg shape, matching C# Vajicka.Egg()
  const p1 = new PdfPoint(left, top + H * 0.55);
  const p2 = new PdfPoint(left + W * 0.1, top - H * 0.15);
  const p3 = new PdfPoint(right - W * 0.1, top - H * 0.15);
  const p4 = new PdfPoint(right, top + H * 0.55);
  const p5 = new PdfPoint(left, bottom + H * 0.15);
  const p6 = new PdfPoint(right, bottom + H * 0.15);

  // drawBeziers format: [start, c1, c2, end, c1, c2, end]
  // Bezier 1 (top arc): p1 → p4 via p2, p3
  // Bezier 2 (bottom arc): p4 → p1 via p6, p5
  page.drawBeziers(pen, brush, [p1, p2, p3, p4, p6, p5, p1]);
}

/**
 * Vajicka (Easter Eggs) - Coloured egg worksheet generator.
 *
 * Page 1: Flow layout — eggs fill the page in rows using a single random
 *         colour palette (10 colours), each egg contains its image label.
 * Pages 2–3: Grid layout — each page is divided into 1×2 sections;
 *            each section holds a 4×2 grid of eggs with shuffled image
 *            assignment. A semi-transparent white overlay is drawn over
 *            every egg so students can colour them in.
 *
 * Migrated from C# EduFrame.Img10.Generators.Vajicka
 */
export class Vajicka implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 8;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 16;
  }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const rnd = opts.data.random;

    const marginOut = PdfUnit.FromMillimeters(10);
    const marginIn = PdfUnit.FromMillimeters(5);
    const marginMin = PdfUnit.FromMillimeters(2);

    // Pick one of the three colour palettes at random
    const palette = ALL_COLORS[rnd.next(0, ALL_COLORS.length)];
    const colors = palette.map((hex) => new PdfBrush(PdfColor.fromHex(hex)));

    // Section grid: 1 column × 2 rows of sections per page
    const s1x = 1,
      s1y = 2;
    // Egg grid: 4 columns × 2 rows of eggs per section
    const s2x = 4,
      s2y = 2;

    const sectionW = PdfSize.A4.width / s1x;
    const sectionH = PdfSize.A4.height / s1y;

    // Cell size inside one section
    const sz1w = (sectionW - 2 * marginOut - (s2x - 1) * marginIn) / s2x;
    const sz1h = (sectionH - 2 * marginOut - (s2y - 1) * marginIn) / s2y;

    // Square inner rect for the image label (80 % of cell width)
    const szxw = sz1w * 0.8;

    // ── Page 1: flow layout ──────────────────────────────────────────────
    const page0 = pdf.addPage();
    let px = marginIn;
    let py = marginIn;
    let ii = 0;

    while (py + sz1h + marginIn <= PdfSize.A4.height) {
      const r = new PdfRect(px, py, sz1w, sz1h);
      const r2 = new PdfRect(r.centerX - szxw * 0.5, r.centerY - szxw * 0.4, szxw, szxw);

      drawEgg(page0, r, colors[ii % 10], GUtil.basePen);
      if (opts.list.length > 0) {
        page0.drawImageCenter(opts.list[ii % opts.list.length].image, r2);
      }

      ii++;
      px += sz1w + marginMin;
      if (px + sz1w + marginIn > PdfSize.A4.width) {
        px = marginIn;
        py += sz1h + marginMin;
      }
    }

    // ── Pages 2–3: grid layout with white overlay ────────────────────────
    for (let pg = 0; pg < 2; pg++) {
      const page = pdf.addPage();

      for (let x1 = 0; x1 < s1x; x1++) {
        for (let y1 = 0; y1 < s1y; y1++) {
          const sectionRect = new PdfRect(x1 * sectionW, y1 * sectionH, sectionW, sectionH);
          page.drawRectangle(GUtil.cutPen, null, sectionRect);

          // Shuffle 10 colour/image indexes for this section
          const indexes = Array.from({ length: opts.list.length }, (_, i) => i);
          for (let i = indexes.length - 1; i > 0; i--) {
            const j = rnd.next(0, i + 1);
            [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
          }

          for (let x2 = 0; x2 < s2x; x2++) {
            for (let y2 = 0; y2 < s2y; y2++) {
              const index = x2 * s2y + y2;
              const imgIndex = indexes[index];

              const rx = marginOut + sectionRect.x + x2 * (sz1w + marginIn);
              const ry = marginOut + sectionRect.y + y2 * (sz1h + marginIn);
              const r = new PdfRect(rx, ry, sz1w, sz1h);
              const r2 = new PdfRect(r.centerX - szxw * 0.5, r.centerY - szxw * 0.4, szxw, szxw);

              drawEgg(page, r, colors[imgIndex % 10], GUtil.basePen);
              if (imgIndex < opts.list.length) {
                page.drawImageCenter(opts.list[imgIndex].image, r2);
              }
              // Draw white overlay so students can colour the egg
              drawEgg(page, r, OVERLAY, null);
            }
          }
        }
      }
    }
  }
}
